import React, { useEffect, useRef, useState } from 'react';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { getViewerTimezone } from '../../utils/dateTime';
import './SmsConsentCard.css';

// Same wording as the registration checkbox (RegisterPage.js): the consent a user gives here must be
// the consent the toll-free verification describes, so keep the two in step.
export const SMS_CONSENT_COPY =
  'Get transaction updates and verification codes from Rialty by text — invitations to join a '
  + 'transaction workspace and one-time codes to verify your identity before viewing or signing '
  + 'documents. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.';

const authHeaders = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${await user.getIdToken()}` };
};

const lastFour = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : '';
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone(),
    });
};

/**
 * "Text messages" setting: lets a user record (or withdraw) their own opt-in to SMS. Existing accounts have
 * no recorded consent, so without this they can never receive invitation texts.
 *
 * Unlike the other toggles on the page this saves immediately and waits for the server's answer: a consent
 * record must never show "on" unless the server stored it.
 */
const SmsConsentCard = () => {
  const [loaded, setLoaded] = useState(false);
  const [consent, setConsent] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.userMe, { headers: await authHeaders() });
        if (!mounted.current) return;
        setConsent(res.data?.smsConsent === true);
        setConsentAt(res.data?.smsConsentAt || null);
        setPhone(res.data?.phone || '');
      } catch (err) {
        console.error('Failed to load SMS consent:', err);
        if (mounted.current) setError('Could not load your text message setting.');
      } finally {
        if (mounted.current) setLoaded(true);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  const handleToggle = async () => {
    if (saving || !loaded) return;
    const next = !consent;
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.patch(
        API_ENDPOINTS.userSmsConsent,
        { consent: next },
        { headers: await authHeaders() },
      );
      if (!mounted.current) return;
      // Show what the server stored, not what we asked for.
      setConsent(res.data?.smsConsent === true);
      setConsentAt(res.data?.smsConsentAt || null);
    } catch (err) {
      console.error('Failed to save SMS consent:', err);
      if (mounted.current) setError('Could not save your choice. Please try again.');
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const tail = lastFour(phone);
  const since = consent && consentAt ? formatDate(consentAt) : '';

  return (
    <div className="nsp-card sms-consent-card">
      <div className="nsp-toggle-row">
        <div className="nsp-toggle-icon-wrap">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div className="nsp-toggle-info">
          <span className="nsp-toggle-label">Text Messages</span>
          <span className="nsp-toggle-desc">
            {consent
              ? `On${since ? ` since ${since}` : ''}${tail ? ` · texts go to the number ending ${tail}` : ''}`
              : 'Off — you will not receive texts from Rialty'}
          </span>
        </div>
        <label className="nsp-toggle">
          <input
            type="checkbox"
            role="switch"
            aria-label="Text messages"
            checked={consent}
            disabled={!loaded || saving}
            onChange={handleToggle}
          />
          <span className="nsp-toggle-track"><span className="nsp-toggle-thumb" /></span>
        </label>
      </div>
      <div className="nsp-divider" />
      <p className="sms-consent-copy">
        {SMS_CONSENT_COPY}{' '}
        <a href="/sms-consent" target="_blank" rel="noopener noreferrer">Learn more</a>.
      </p>
      {loaded && !phone && (
        <p className="sms-consent-note">
          Add a mobile number to your profile so texts can reach you.
        </p>
      )}
      {error && <p className="sms-consent-error" role="alert">{error}</p>}
    </div>
  );
};

export default SmsConsentCard;
